/**
 * SingleImageMode V2
 *
 * Haupt-Komponente für die Einzelbild-Generierung.
 *
 * Layout (wie V1):
 * - MITTE: "Meine Bilder" Gallery (flex: 1, scrollbar)
 * - RECHTS: Generation Form (360px, sticky)
 *
 * Features:
 * - "Magie passiert" Platzhalter während Generierung
 * - Featured Image nach erfolgreicher Generierung
 * - Mehrfachauswahl mit Bulk-Aktionen
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useCallback, useMemo } from 'react';
import { Images } from 'lucide-react';
import { useSimpleGallery } from '@/hooks/useGalleryV2';
import { galleryImagesToLegacy } from '@/lib/api/gallery-v2';
import { Badge } from '@/components/v2/ui';
import { GenerationForm } from '@/components/v2/tools/generation';
import { ImageGallery } from '@/components/v2/tools/gallery';

export function SingleImageMode() {
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(12);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showFeatured, setShowFeatured] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [generationStartTime, setGenerationStartTime] = useState<Date | undefined>(undefined);

  const { images: galleryImages, pagination, isLoading, isFetching, refetch } = useSimpleGallery({
    page: currentPage,
    perPage,
  });

  // Konvertiere GalleryImages zu Legacy-Format für ImageGallery-Komponente
  const images = useMemo(
    () => galleryImagesToLegacy(galleryImages),
    [galleryImages]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setShowFeatured(false);
    setSelectedIds(new Set());
  };

  const handleGenerationStart = useCallback(() => {
    setIsGenerating(true);
    setCurrentPage(1);
    setGenerationStartTime(new Date());
  }, []);

  const handleGenerationSuccess = useCallback(() => {
    setIsGenerating(false);
    setShowFeatured(true);
    setGenerationStartTime(undefined);
    refetch();
  }, [refetch]);

  const handleGenerationError = useCallback(() => {
    setIsGenerating(false);
    setGenerationStartTime(undefined);
  }, []);

  const handleSelectionChange = useCallback((ids: Set<string>) => {
    setSelectedIds(ids);
  }, []);

  return (
    <div className="flex h-full">
      {/* MITTE: Gallery Section - Scrollable */}
      <section className="flex-1 overflow-y-auto p-6">
        {/* Gallery Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            <Images className="h-5 w-5 text-orange-500" />
            Meine Bilder
            {pagination && (
              <Badge variant="secondary">{pagination.total_images}</Badge>
            )}
          </h2>
        </div>

        {/* Gallery Content */}
        <ImageGallery
          images={images || []}
          isLoading={isLoading}
          isFetching={isFetching}
          pagination={pagination}
          onPageChange={handlePageChange}
          isGenerating={isGenerating}
          generationStartTime={generationStartTime}
          showFeatured={showFeatured}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
        />
      </section>

      {/* RECHTS: Generation Form - Fixed Width, Sticky */}
      <aside className="w-[360px] flex-shrink-0 border-l border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 overflow-y-auto">
        <GenerationForm
          onGenerationStart={handleGenerationStart}
          onSuccess={handleGenerationSuccess}
          onError={handleGenerationError}
        />
      </aside>
    </div>
  );
}

export default SingleImageMode;
